/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Minimal X.509 v3 certificate parser (RFC 5280) producing the fields C2PA
 * reports in `signature_info` plus the SubjectPublicKeyInfo bytes for WebCrypto
 * key import. Not a general-purpose / security-hardened parser.
 */

import { readTlv, readChildren, content, tlvBytes, decodeOid, decodeIntegerDecimal, decodeSmallNonNegInt, type Tlv } from './der'

export interface CertInfo {
    /** Serial number as an unsigned decimal string. */
    serialNumber: string
    /** Issuer attributes (e.g. CN, O). */
    issuer: Name
    /** Subject attributes. */
    subject: Name
    /** notBefore as an ISO-8601 string. */
    notBefore: string
    /** notAfter as an ISO-8601 string. */
    notAfter: string
    /** Raw DER of the SubjectPublicKeyInfo SEQUENCE (for crypto.subtle importKey 'spki'). */
    spki: Uint8Array
    /** signatureAlgorithm OID of the certificate itself. */
    signatureAlgOid: string
    /** Raw DER bytes of the tbsCertificate (the signed-over content). */
    tbs: Uint8Array
    /** The certificate's signature bits. */
    signature: Uint8Array
    /** Raw DER of the issuer Name (for chain building by exact DN match). */
    issuerDer: Uint8Array
    /** Raw DER of the subject Name. */
    subjectDer: Uint8Array
    /** Extended Key Usage OIDs (empty if the extension is absent). */
    eku: string[]
    /** True if BasicConstraints marks this a CA certificate. */
    isCa: boolean
    /** BasicConstraints pathLenConstraint, when present (RFC 5280 §4.2.1.9). */
    pathLen?: number
    /** X.509 version number: 1, 2, or 3 (RFC 5280 encodes these as 0/1/2). */
    version: number
    /** True if the Extended Key Usage extension is present (distinguishes absent from empty). */
    hasEku: boolean
    /** SubjectPublicKeyInfo algorithm OID (e.g. RSA, ecPublicKey, RSASSA-PSS, Ed25519). */
    spkiAlgOid: string
    /** EC named-curve OID (only for ecPublicKey SPKI). */
    ecCurveOid?: string
    /** RSA/PSS modulus bit length (only for RSA/PSS SPKI). */
    rsaModulusBits?: number
    /** Raw DER of the certificate signatureAlgorithm's parameters (for RSASSA-PSS param checks). */
    sigAlgParams?: Uint8Array
    /** Key Usage bits (from 2.5.29.15), when the extension is present. */
    keyUsage?: KeyUsage
    /** Authority Key Identifier extension present. */
    hasAki: boolean
    /** Subject Key Identifier extension present. */
    hasSki: boolean
    /** issuerUniqueID or subjectUniqueID present in tbsCertificate (disallowed by the C2PA profile). */
    hasUniqueId: boolean
    /** True if a *critical* extension was present whose OID this parser does not recognize. */
    unhandledCriticalExt: boolean
}

export interface KeyUsage {
    digitalSignature: boolean
    nonRepudiation: boolean
    keyCertSign: boolean
}

export interface Name {
    /** Common Name (2.5.4.3). */
    CN?: string
    /** Organization (2.5.4.10). */
    O?: string
    /** Organizational Unit (2.5.4.11). */
    OU?: string
    /** Country (2.5.4.6). */
    C?: string
    /** All attributes, keyed by OID. */
    attrs: Record<string, string>
}

const OID = {
    CN: '2.5.4.3',
    O: '2.5.4.10',
    OU: '2.5.4.11',
    C: '2.5.4.6'
} as const

const textDecoder = new TextDecoder()

function parseName (u8: Uint8Array, nameTlv: Tlv): Name {
    const name: Name = { attrs: {} }
    // Name ::= SEQUENCE OF RDN ::= SET OF AttributeTypeAndValue { type OID, value }
    for (const rdn of readChildren(u8, nameTlv.contentStart, nameTlv.contentEnd)) {
        for (const atv of readChildren(u8, rdn.contentStart, rdn.contentEnd)) {
            const [typeTlv, valueTlv] = readChildren(u8, atv.contentStart, atv.contentEnd)
            if (!typeTlv || !valueTlv) continue
            const oid = decodeOid(content(u8, typeTlv))
            const value = textDecoder.decode(content(u8, valueTlv))
            name.attrs[oid] = value
            if (oid === OID.CN) name.CN = value
            else if (oid === OID.O) name.O = value
            else if (oid === OID.OU) name.OU = value
            else if (oid === OID.C) name.C = value
        }
    }
    return name
}

/** Parses a DER Time (UTCTime/GeneralizedTime) to an ISO-8601 string. */
function parseTime (u8: Uint8Array, t: Tlv): string {
    const s = textDecoder.decode(content(u8, t))
    // UTCTime: YYMMDDHHMMSSZ ; GeneralizedTime: YYYYMMDDHHMMSSZ
    let y: string, rest: string
    if (t.tag === 0x17) {
        const yy = Number(s.slice(0, 2))
        y = String(yy >= 50 ? 1900 + yy : 2000 + yy)
        rest = s.slice(2)
    } else {
        y = s.slice(0, 4)
        rest = s.slice(4)
    }
    const mo = rest.slice(0, 2), d = rest.slice(2, 4), h = rest.slice(4, 6), mi = rest.slice(6, 8)
    const sec = rest.slice(8, 10) || '00'
    return `${y}-${mo}-${d}T${h}:${mi}:${sec}+00:00`
}

const OID_EKU = '2.5.29.37'
const OID_BASIC_CONSTRAINTS = '2.5.29.19'
const OID_KEY_USAGE = '2.5.29.15'
const OID_AKI = '2.5.29.35'
const OID_SKI = '2.5.29.14'
const OID_EC_PUBLICKEY = '1.2.840.10045.2.1'
const OID_RSA = '1.2.840.113549.1.1.1'
const OID_RSASSA_PSS = '1.2.840.113549.1.1.10'

/**
 * Extension OIDs whose presence — even when critical — this parser understands, so
 * they do NOT trip the "unhandled critical extension" profile check. Mirrors the
 * ParsedExtension arms c2pa-rs treats as recognized in `certificate_profile.rs`.
 */
const RECOGNIZED_EXT_OIDS = new Set<string>([
    OID_BASIC_CONSTRAINTS, OID_KEY_USAGE, OID_EKU, OID_AKI, OID_SKI,
    '2.5.29.32', // certificatePolicies
    '2.5.29.33', // policyMappings
    '2.5.29.17', // subjectAltName
    '2.5.29.30', // nameConstraints
    '2.5.29.36', // policyConstraints
    '2.5.29.31', // cRLDistributionPoints
    '2.5.29.54', // inhibitAnyPolicy
    '1.3.6.1.5.5.7.1.1', // authorityInfoAccess
    '2.16.840.1.113730.1.1', // nsCertType
    '2.5.29.20', // cRLNumber
    '2.5.29.21', // reasonCode
    '2.5.29.24' // invalidityDate
])

interface ParsedExtensions {
    eku: string[]
    hasEku: boolean
    isCa: boolean
    pathLen?: number
    keyUsage?: KeyUsage
    hasAki: boolean
    hasSki: boolean
    unhandledCriticalExt: boolean
}

/** Parses the extensions ([3] EXPLICIT SEQUENCE OF Extension). */
function parseExtensions (der: Uint8Array, tbsChildren: Tlv[], afterIndex: number): ParsedExtensions {
    const out: ParsedExtensions = { eku: [], hasEku: false, isCa: false, hasAki: false, hasSki: false, unhandledCriticalExt: false }
    const extWrapper = tbsChildren.slice(afterIndex).find(t => t.tag === 0xa3)
    if (!extWrapper) return out
    const seq = readChildren(der, extWrapper.contentStart, extWrapper.contentEnd)[0]
    if (!seq) return out
    for (const ext of readChildren(der, seq.contentStart, seq.contentEnd)) {
        const parts = readChildren(der, ext.contentStart, ext.contentEnd)
        if (!parts[0]) continue // malformed Extension with no type OID → skip (X509-1)
        const oid = decodeOid(content(der, parts[0]))
        const critical = parts[1]?.tag === 0x01 ? content(der, parts[1])[0] !== 0x00 : false
        const octet = parts.find(p => p.tag === 0x04) // extnValue OCTET STRING
        if (!octet) continue
        if (oid === OID_EKU) {
            out.hasEku = true
            const inner = readTlv(der, octet.contentStart) // SEQUENCE OF OID
            for (const o of readChildren(der, inner.contentStart, inner.contentEnd)) {
                out.eku.push(decodeOid(content(der, o)))
            }
        } else if (oid === OID_BASIC_CONSTRAINTS) {
            const inner = readTlv(der, octet.contentStart) // SEQUENCE { cA BOOLEAN DEFAULT FALSE, pathLenConstraint INTEGER OPTIONAL }
            for (const c of readChildren(der, inner.contentStart, inner.contentEnd)) {
                if (c.tag === 0x01) out.isCa = content(der, c)[0] !== 0x00 // cA BOOLEAN
                else if (c.tag === 0x02) out.pathLen = decodeSmallNonNegInt(content(der, c)) // pathLenConstraint INTEGER (X509-6: full-width, non-negative)
            }
        } else if (oid === OID_KEY_USAGE) {
            const bits = readTlv(der, octet.contentStart) // BIT STRING
            const c = content(der, bits)
            const b0 = c.length > 1 ? c[1] : 0 // byte after the unused-bits count
            out.keyUsage = {
                digitalSignature: (b0 & 0x80) !== 0,
                nonRepudiation: (b0 & 0x40) !== 0,
                keyCertSign: (b0 & 0x04) !== 0
            }
        } else if (oid === OID_AKI) {
            out.hasAki = true
        } else if (oid === OID_SKI) {
            out.hasSki = true
        } else if (critical && !RECOGNIZED_EXT_OIDS.has(oid)) {
            out.unhandledCriticalExt = true
        }
    }
    return out
}

/** Parses a SubjectPublicKeyInfo SEQUENCE for the algorithm, EC curve, and RSA modulus size. */
function parseSpki (spki: Uint8Array): { algOid: string, ecCurveOid?: string, rsaModulusBits?: number } {
    const seq = readTlv(spki, 0)
    const [algId, bitString] = readChildren(spki, seq.contentStart, seq.contentEnd)
    const algParts = readChildren(spki, algId.contentStart, algId.contentEnd)
    const algOid = decodeOid(content(spki, algParts[0]))
    const result: { algOid: string, ecCurveOid?: string, rsaModulusBits?: number } = { algOid }
    if (algOid === OID_EC_PUBLICKEY && algParts[1]?.tag === 0x06) {
        result.ecCurveOid = decodeOid(content(spki, algParts[1]))
    } else if (algOid === OID_RSA || algOid === OID_RSASSA_PSS) {
        // subjectPublicKey BIT STRING → RSAPublicKey SEQUENCE { modulus INTEGER, exponent }
        const bits = content(spki, bitString).subarray(1) // drop unused-bits count
        const rsaSeq = readTlv(bits, 0)
        const modulus = readChildren(bits, rsaSeq.contentStart, rsaSeq.contentEnd)[0]
        let m = content(bits, modulus)
        let z = 0
        while (z < m.length && m[z] === 0x00) z++ // strip DER sign padding
        m = m.subarray(z)
        result.rsaModulusBits = m.length === 0 ? 0 : (m.length - 1) * 8 + (32 - Math.clz32(m[0]))
    }
    return result
}

/** Parses a single DER-encoded X.509 certificate. */
export function parseCertificate (der: Uint8Array): CertInfo {
    const cert = readTlv(der, 0) // Certificate ::= SEQUENCE
    const [tbs, sigAlg, sigValue] = readChildren(der, cert.contentStart, cert.contentEnd)
    const tbsChildren = readChildren(der, tbs.contentStart, tbs.contentEnd)

    // tbsCertificate fields, accounting for the optional [0] version wrapper.
    let i = 0
    let version = 1 // RFC 5280: absent [0] wrapper → v1
    if (tbsChildren[0].tag === 0xa0) {
        const verInt = readTlv(der, tbsChildren[0].contentStart)
        version = (content(der, verInt)[0] ?? 0) + 1 // encoded 0/1/2 → v1/v2/v3
        i = 1
    }
    const serialTlv = tbsChildren[i++]
    i++ // signature AlgorithmIdentifier
    const issuerTlv = tbsChildren[i++]
    const validityTlv = tbsChildren[i++]
    const subjectTlv = tbsChildren[i++]
    const spkiTlv = tbsChildren[i++]
    // Optional issuerUniqueID [1] / subjectUniqueID [2] precede the [3] extensions.
    const hasUniqueId = tbsChildren.slice(i).some(t => t.tag === 0x81 || t.tag === 0x82)

    const [notBeforeTlv, notAfterTlv] = readChildren(der, validityTlv.contentStart, validityTlv.contentEnd)
    const sigAlgParts = readChildren(der, sigAlg.contentStart, sigAlg.contentEnd)
    const sigAlgOidTlv = sigAlgParts[0]
    const ext = parseExtensions(der, tbsChildren, i)
    const spkiInfo = parseSpki(tlvBytes(der, spkiTlv))

    // signatureValue is a BIT STRING: first content byte is the unused-bits count.
    const sigBits = content(der, sigValue)

    return {
        serialNumber: decodeIntegerDecimal(content(der, serialTlv)),
        issuer: parseName(der, issuerTlv),
        subject: parseName(der, subjectTlv),
        notBefore: parseTime(der, notBeforeTlv),
        notAfter: parseTime(der, notAfterTlv),
        spki: tlvBytes(der, spkiTlv),
        signatureAlgOid: sigAlgOidTlv ? decodeOid(content(der, sigAlgOidTlv)) : '',
        sigAlgParams: sigAlgParts[1] ? tlvBytes(der, sigAlgParts[1]) : undefined,
        tbs: tlvBytes(der, tbs),
        signature: sigBits.subarray(1),
        issuerDer: tlvBytes(der, issuerTlv),
        subjectDer: tlvBytes(der, subjectTlv),
        eku: ext.eku,
        isCa: ext.isCa,
        pathLen: ext.pathLen,
        version,
        hasEku: ext.hasEku,
        spkiAlgOid: spkiInfo.algOid,
        ecCurveOid: spkiInfo.ecCurveOid,
        rsaModulusBits: spkiInfo.rsaModulusBits,
        keyUsage: ext.keyUsage,
        hasAki: ext.hasAki,
        hasSki: ext.hasSki,
        hasUniqueId,
        unhandledCriticalExt: ext.unhandledCriticalExt
    }
}
